/**
 * Cosmetics v3 botanical intro: vanilla custom element, no dependencies.
 *
 * Drives the decorative motion through CSS custom properties.
 *  - Scroll-linked modes (reference, parallax, rotate) write --cv3-progress
 *    inside requestAnimationFrame, and only while the section is near the viewport.
 *  - Entrance modes (slide, fade-slide) toggle is-visible once with IntersectionObserver.
 * Reduced motion disables all modes. Listeners are removed on disconnect, so Theme Editor
 * section reloads never double-attach handlers.
 */
class Cv3BotanicalIntro extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.mode = this.dataset.mode || 'disabled';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.reducedMotion) this.mode = 'disabled';

    this.start = Number(this.dataset.progressStart) / 100 || 0;
    this.end = Number(this.dataset.progressEnd) / 100 || 1;
    if (this.end <= this.start) this.end = this.start + 0.01;

    this.frame = null;
    this.observer = null;

    if (this.mode === 'disabled') return;

    if (this.mode === 'slide' || this.mode === 'fade-slide') {
      this.setupEntrance();
    } else {
      this.setupScrollLinked(signal);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.observer?.disconnect();
    this.observer = null;
  }

  setupEntrance() {
    this.section = this.closest('.cv3-intro-section') || this;
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.section.classList.add('is-visible');
          this.observer.disconnect();
        }
      });
    }, { threshold: 0.2 });
    this.observer.observe(this);
  }

  setupScrollLinked(signal) {
    this.section = this.closest('.cv3-intro-section') || this;
    this.schedule = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateProgress();
      });
    };
    // Horizon scrolls inside .page-wrapper, so listen in the capture phase on document to catch any scroll container.
    document.addEventListener('scroll', this.schedule, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.schedule, { passive: true, signal });
    this.updateProgress();
  }

  updateProgress() {
    // Measure the full-width section, not the content box, so progress matches the decoration layer.
    const target = this.section || this;
    const rect = target.getBoundingClientRect();
    const vh = window.innerHeight;
    // 0 when the section's top enters at the bottom of the viewport, 1 when its bottom leaves at the top.
    const raw = (vh - rect.top) / (vh + rect.height);
    const clamped = Math.min(1, Math.max(0, raw));
    const ranged = Math.min(1, Math.max(0, (clamped - this.start) / (this.end - this.start)));
    const value = ranged.toFixed(4);
    // Write only when the value changes, so idle scrolling does no style work.
    if (value !== this.lastProgress) {
      this.lastProgress = value;
      // Progress is set on the section, which contains the decoration layer.
      (this.section || this).style.setProperty('--cv3-progress', value);
    }
  }
}

if (!customElements.get('cv3-botanical-intro')) {
  customElements.define('cv3-botanical-intro', Cv3BotanicalIntro);
}
